import type { SizeLocales } from "@lib/constants/filtersConstants";
import { MAX_SHOE_SIZE, MIN_SHOE_SIZE, ONE_SIZE } from "@lib/constants/filtersConstants";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getProductAttributeValue } from "./attributesUtils";
import type { ProductVariant as RawProductVariant } from "@graphql/generated/types";
import type { ProductChannel, ProductVariant } from "@lib/product/types";

export const isShoeSize = (item: string | number) => {
    return !isNaN(Number(item)) && Number(item) <= MAX_SHOE_SIZE && Number(item) >= MIN_SHOE_SIZE;
};

export const hasOneSize = (item?: string | number) => {
    if (typeof item === "string") {
        return item?.toLowerCase().includes(ONE_SIZE);
    } else {
        return false;
    }
};

export const getVariantAttribute = (
    variant: ProductVariant | RawProductVariant,
    name: Attributes,
    locale?: string
) => {
    return getProductAttributeValue(variant?.attributesRaw, name, locale);
};

export const getSizeAttributeByLocale = (
    variant: ProductVariant | RawProductVariant,
    sizeLocale: string
): string | undefined => {
    const sizeAtt = sizeLocale === "EU" ? "Size" : `Size_${sizeLocale}`;
    const result = getProductAttributeValue(variant?.attributesRaw, sizeAtt as Attributes);

    if (typeof result === "string") {
        return result;
    }
    const alternativeSize = getProductAttributeValue(variant?.attributesRaw, Attributes.size);
    if (typeof alternativeSize === "string") {
        return alternativeSize;
    }
};

export const getAvailability = (availableChannels: ProductChannel[], supplyChannelId?: string) => {
    const findAvailableChannel = (channelId?: string) =>
        availableChannels?.find((channel) => channel.id === channelId);

    const storeAvailability = findAvailableChannel(supplyChannelId);
    if (storeAvailability?.isOnStock) return storeAvailability;

    const warehouseAvailability = findAvailableChannel(process.env.NEXT_PUBLIC_CHANNEL_ID);
    return warehouseAvailability?.isOnStock ? warehouseAvailability : null;
};

export const isClickAndCollect = (supplyChannelId: string) =>
    supplyChannelId !== process.env.NEXT_PUBLIC_CHANNEL_ID;

export const makeNonShoeSizePrettySize = (size: string): string => {
    const exceptionSizes = ["100", "110", "120", "130", "140", "150"];
    if (exceptionSizes.includes(size)) {
        return size;
    }

    const attemptedPrettySize = Array.from(size).reduce((str, val, index) => {
        return str + (index === 2 ? `-${val}` : val);
    }, "");
    const exceptions = ["--", "-", ".-", "'", "."];
    const hasException = exceptions.some((exception) => size.includes(exception));

    if (hasException) {
        return size;
    }

    return attemptedPrettySize;
};

export const isSizeProbablyASockSize = (size: string): boolean => Number(size) > 999;

export const getProductSizeLabel = ({
    variant,
    shoeConvention,
    affix,
}: {
    variant?: ProductVariant | RawProductVariant;
    shoeConvention?: SizeLocales;
    affix?: string;
}) => {
    const fallback = {
        label: undefined,
        rawLabel: undefined,
    };
    if (!variant || !shoeConvention) {
        return fallback;
    }
    const productSize = getSizeAttributeByLocale(variant, shoeConvention);
    if (!productSize) {
        return fallback;
    }
    let label = productSize;
    const isProbablySock = isSizeProbablyASockSize(productSize);
    if (isProbablySock) {
        label = makeNonShoeSizePrettySize(productSize);
    }

    const withAffix = affix ? `${label} ${affix}` : label;

    return {
        label: withAffix,
        rawLabel: label,
    };
};
